/**
 * The pipeline editor. `Edit Stages` in the wireframe, beside the selector.
 *
 * ┌─ ⛔ THE FORM'S SHAPE IS NOT A DESIGN CHOICE, IT IS A CONSTRAINT ────────────┐
 * │ `pipelines_require_terminal_stages_trigger` is DEFERRABLE INITIALLY         │
 * │ DEFERRED, so it fires at COMMIT and demands a won stage and a lost stage.   │
 * │ **"Create the pipeline, then add stages to it" cannot work** - the first    │
 * │ step would not commit.                                                     │
 * │                                                                            │
 * │ ⚠️ SO THIS IS ONE FORM WITH THE STAGES IN IT, SUBMITTED ONCE. Which is also  │
 * │ the better interaction: a pipeline with no stages was never a thing anybody │
 * │ wanted to look at.                                                         │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ EVERY DESTRUCTIVE CONTROL SHOWS ITS COUNT BEFORE IT IS PRESSED. Removing a
 * stage that holds bookings and deleting a pipeline that holds bookings are both
 * refused, and the refusal is only actionable if the number is on screen: *"this
 * holds 104 bookings"* names the thing to do next where *"this cannot be
 * deleted"* is a wall.
 */
import { useEffect, useRef, useState } from 'react'
import {
  createPipeline,
  deletePipeline,
  fetchPipelines,
  renamePipeline,
  saveStages,
  type EditorPipeline,
  type EditorStage,
  type StageKind,
} from '../data/pipelines-api.js'

const KINDS: Array<{ key: StageKind; label: string }> = [
  { key: 'active', label: 'Open' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
]

/** ⚠️ A new pipeline starts with the three the trigger requires, already typed.
 *  Starting empty would let somebody build a shape the database refuses and
 *  discover it on submit. */
const STARTER: DraftStage[] = [
  { key: 'a', label: 'Enquiry', kind: 'active', colour: null, bookingCount: 0 },
  { key: 'b', label: 'Won', kind: 'won', colour: null, bookingCount: 0 },
  { key: 'c', label: 'Lost', kind: 'lost', colour: null, bookingCount: 0 },
]

type DraftStage = {
  /** ⚠️ The server id where there is one, else a local key. The distinction is
   *  what tells `setStages` to UPDATE rather than insert, which is what keeps a
   *  stage's bookings attached to it. */
  id?: string
  key: string
  label: string
  kind: StageKind
  colour: string | null
  bookingCount: number
}

export function PipelineEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: () => void
}) {
  const [pipelines, setPipelines] = useState<EditorPipeline[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [stages, setStages] = useState<DraftStage[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** ⛔ The wireframe's own sentence, raised by the server's refusal rather than
   *  answered before it: *"Two bookings are in this stage. Choose where they go
   *  before deleting. This cannot be undone."* */
  const [rehoming, setRehoming] = useState<{ count: number } | null>(null)
  const [rehomeTo, setRehomeTo] = useState('')
  const first = useRef<HTMLInputElement | null>(null)

  const load = async (select?: string) => {
    const list = await fetchPipelines()
    setPipelines(list)
    const pick = select ?? chosen ?? list[0]?.id ?? null
    setChosen(pick)
    const p = list.find((x) => x.id === pick)
    setLabel(p?.label ?? '')
    setStages((p?.stages ?? []).map(asDraft))
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    first.current?.focus()
  }, [pipelines === null])

  const choose = (id: string | null) => {
    setProblem(null)
    setChosen(id)
    if (id === null) {
      /** ⛔ THE NEW-PIPELINE DRAFT. Not saved until submitted, so cancelling
       *  leaves nothing behind - which a two-step create could not promise. */
      setLabel('')
      setStages(STARTER.map((s) => ({ ...s })))
      return
    }
    const p = pipelines?.find((x) => x.id === id)
    setLabel(p?.label ?? '')
    setStages((p?.stages ?? []).map(asDraft))
  }

  const move = (index: number, delta: number) => {
    const to = index + delta
    if (to < 0 || to >= stages.length) return
    const next = stages.slice()
    const a = next[index]!
    next[index] = next[to]!
    next[to] = a
    setStages(next)
  }

  /**
   * ┌─ ⛔ IT NO LONGER ANSWERS THE SERVER'S QUESTION ON THE PERSON'S BEHALF ─────┐
   * │ Amir's defect 3, 2026-09-16: removing a stage that held bookings moved them │
   * │ **with no prompt**, and he could not tell where they went.                  │
   * │                                                                            │
   * │ ⛔ THE SERVICE WAS RIGHT AND THE CLIENT WAS WRONG. `setStages` refuses with │
   * │ `blocked-by-bookings` and the count unless a destination is given - which is │
   * │ exactly the wireframe's *"Choose where they go before deleting"*. The first │
   * │ version of this file called `orphanTargetFor`, which PICKED the first        │
   * │ surviving open stage and passed it, so the refusal was never reached.      │
   * │                                                                            │
   * │ ⚠️ AND THE TEST PASSED THROUGHOUT, because it asserts the SERVICE refuses.  │
   * │ It does. **A guard the client can answer for itself is a guard nobody       │
   * │ meets**, and nothing asserted that a person ever sees the question.        │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  const save = async (moveOrphansTo?: string) => {
    setBusy(true)
    setProblem(null)
    const payload = stages.map((s) => ({
      ...(s.id === undefined ? {} : { id: s.id }),
      label: s.label,
      kind: s.kind,
      colour: s.colour,
    }))

    const result =
      chosen === null
        ? await createPipeline(label, payload)
        : await saveStages(chosen, payload, moveOrphansTo)
    if (result.ok && chosen !== null) {
      /** ⚠️ The rename is a second call because it is a second verb. A combined
       *  endpoint would make renaming a pipeline require sending its stages. */
      await renamePipeline(chosen, label)
    }
    setBusy(false)

    if (!result.ok) {
      /** ⛔ THE REFUSAL BECOMES A QUESTION rather than a sentence, because it is
       *  the one refusal with an answer the person has to give. */
      if (result.reason === 'blocked-by-bookings') {
        setRehoming({ count: result.count ?? 0 })
        return
      }
      setProblem(sentenceFor(result))
      return
    }
    await load(result.id ?? chosen ?? undefined)
    onSaved()
    /** ⛔ AND IT CLOSES. Amir's defect 2: Cancel closed and Save did not, which
     *  is the columns popover's shape for the second time. A dialog that stays
     *  open after a successful save reads as a save that did not happen. */
    onClose()
  }

  const remove = async () => {
    if (chosen === null) return
    setBusy(true)
    setProblem(null)
    const result = await deletePipeline(chosen)
    setBusy(false)
    if (!result.ok) {
      setProblem(sentenceFor(result))
      return
    }
    setChosen(null)
    await load()
    onSaved()
  }

  const current = pipelines?.find((p) => p.id === chosen)

  return (
    <div
      className="board__editor-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Pipelines and Stages"
      data-testid="pipeline-editor"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className="board__editor">
        <h2 className="board__editor-title">Pipelines and Stages</h2>

        <label className="board__editor-toggle" htmlFor="pipeline-editor-pick">
          Pipeline
          <select
            id="pipeline-editor-pick"
            data-testid="pipeline-pick"
            value={chosen ?? ''}
            onChange={(e) => choose(e.target.value === '' ? null : e.target.value)}
          >
            {(pipelines ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
            <option value="">New Pipeline…</option>
          </select>
        </label>

        <label className="board__editor-toggle" htmlFor="pipeline-editor-name">
          Name
          <input
            id="pipeline-editor-name"
            ref={first}
            data-testid="pipeline-name"
            value={label}
            maxLength={60}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>

        <ul className="board__editor-slots" data-testid="pipeline-stages">
          {stages.map((s, index) => (
            <li key={s.key} className="board__editor-slot" data-stage-kind={s.kind}>
              <input
                className="board__editor-stage-label"
                data-testid={`stage-label-${index}`}
                value={s.label}
                maxLength={60}
                aria-label={`Stage ${index + 1} Name`}
                onChange={(e) =>
                  setStages(stages.map((x, i) => (i === index ? { ...x, label: e.target.value } : x)))
                }
              />
              <select
                data-testid={`stage-kind-${index}`}
                value={s.kind}
                aria-label={`Stage ${index + 1} Kind`}
                onChange={(e) =>
                  setStages(
                    stages.map((x, i) => (i === index ? { ...x, kind: e.target.value as StageKind } : x)),
                  )
                }
              >
                {KINDS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
              {/** ⛔ THE COUNT, BEFORE ANYTHING IS PRESSED. The wireframe's own
               *  sentence is made of it: *"Two bookings are in this stage.
               *  Choose where they go before deleting."* */}
              <span className="board__editor-count" data-testid={`stage-count-${index}`}>
                {s.bookingCount === 1 ? '1 booking' : `${s.bookingCount} bookings`}
              </span>
              <span className="board__editor-move">
                <button
                  type="button"
                  disabled={index === 0}
                  aria-label={`Move ${s.label} Up`}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={index === stages.length - 1}
                  aria-label={`Move ${s.label} Down`}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  data-testid={`stage-remove-${index}`}
                  aria-label={`Remove ${s.label}`}
                  onClick={() => setStages(stages.filter((_, i) => i !== index))}
                >
                  ×
                </button>
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid="stage-add"
          onClick={() =>
            setStages([
              ...stages,
              { key: `new-${Date.now()}`, label: 'New Stage', kind: 'active', colour: null, bookingCount: 0 },
            ])
          }
        >
          Add a Stage
        </button>

        {problem !== null && (
          <p className="board__editor-error" role="alert" data-testid="pipeline-problem">
            {problem}
          </p>
        )}

        <div className="board__editor-actions">
          {/** ⛔ DELETE IS ONLY OFFERED FOR A SAVED PIPELINE, and it says what it
           *  holds. A draft has nothing to delete. */}
          {chosen !== null && (
            <button
              type="button"
              className="board__editor-delete"
              data-testid="pipeline-delete"
              disabled={busy}
              onClick={() => void remove()}
            >
              {current !== undefined && current.bookingCount > 0
                ? `Delete (holds ${current.bookingCount})`
                : 'Delete'}
            </button>
          )}
          <button type="button" onClick={onClose} data-testid="pipeline-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="board__editor-save"
            disabled={busy}
            data-testid="pipeline-save"
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : chosen === null ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      {/**
       * ⛔ THE QUESTION THE SERVER ASKED, PUT TO THE PERSON. Amir's defect 3.
       * It is a PICKER rather than a confirm, because the refusal's answer is a
       * destination and not a yes: *"Choose where they go before deleting."*
       *
       * ⚠️ IT OFFERS ONLY STAGES THAT SURVIVE THIS EDIT, because a destination
       * this same save deletes would move the bookings to a stage that is gone
       * two statements later. The server refuses that too; this makes it
       * unpickable rather than refusable.
       */}
      {rehoming !== null && (
        <div className="board__editor-scrim" role="dialog" aria-modal="true" data-testid="rehome">
          <div className="board__editor">
            <h2 className="board__editor-title">Where Do These Bookings Go?</h2>
            <p className="board__editor-note" data-testid="rehome-count">
              {rehoming.count === 1
                ? 'One booking is in a stage you are removing.'
                : `${rehoming.count} bookings are in a stage you are removing.`}{' '}
              Choose where they go. This cannot be undone.
            </p>
            <label className="board__editor-toggle" htmlFor="rehome-to">
              Move them to
              <select
                id="rehome-to"
                data-testid="rehome-to"
                value={rehomeTo}
                onChange={(e) => setRehomeTo(e.target.value)}
              >
                <option value="">Choose a stage…</option>
                {stages
                  .filter((s) => s.id !== undefined)
                  .map((s) => (
                    <option key={s.key} value={s.id}>
                      {s.label}
                    </option>
                  ))}
              </select>
            </label>
            <div className="board__editor-actions">
              <button
                type="button"
                data-testid="rehome-cancel"
                onClick={() => {
                  setRehoming(null)
                  setRehomeTo('')
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="board__editor-save"
                disabled={rehomeTo === '' || busy}
                data-testid="rehome-confirm"
                onClick={() => {
                  const to = rehomeTo
                  setRehoming(null)
                  setRehomeTo('')
                  void save(to)
                }}
              >
                Move Them and Delete the Stage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function asDraft(s: EditorStage): DraftStage {
  return { id: s.id, key: s.id, label: s.label, kind: s.kind, colour: s.colour, bookingCount: s.bookingCount }
}

/**
 * ⛔ `orphanTargetFor` WAS DELETED HERE ON 2026-09-16, NOT DISABLED.
 *
 * It picked the first surviving open stage and passed it as `moveOrphansTo`, so
 * the server's refusal - and the wireframe's *"Choose where they go"* - was
 * never reached. Amir's defect 3: bookings moved with no prompt and he could not
 * tell where.
 *
 * ⚠️ IT IS DELETED RATHER THAN LEFT UNCALLED because a helper that still
 * computes the right-looking answer is one edit away from being wired back in,
 * and this project has a name for a guard with no caller.
 * `a-rule-asserted-where-it-is-enforced-is-not-a-rule-anybody-meets`.
 *
 * **The destination now
 * comes from a person, or the save does not happen.**
 */

/**
 * ⛔ THE SERVER'S REFUSAL, IN THE PRODUCT'S WORDS. The reason travels as a code
 * with its numbers so this can say the sentence; reverse-engineering one from a
 * message string would put the phrasing on the wrong side of the wire.
 */
function sentenceFor(r: { reason?: string; count?: number; kind?: string }): string {
  switch (r.reason) {
    case 'blocked-by-bookings':
      return r.count === 1
        ? 'One booking is here. Move it before deleting this. This cannot be undone.'
        : `${r.count} bookings are here. Choose where they go before deleting this. This cannot be undone.`
    case 'needs-won-and-lost':
      return 'A pipeline needs one stage that means won and one that means lost, so reporting has something to count.'
    case 'last-terminal-stage':
      return `This is the only stage that means ${r.kind === 'won' ? 'won' : 'lost'}. Mark another one first.`
    case 'duplicate-terminal-stage':
      return `A pipeline can have only one stage that means ${r.kind === 'won' ? 'won' : 'lost'}.`
    case 'needs-an-active-stage':
      return 'A pipeline needs at least one open stage, or its board has no columns.'
    case 'label-required':
      return 'Every pipeline and every stage needs a name, up to 60 characters.'
    case 'not-found':
      return 'That pipeline no longer exists. Close this and reopen it.'
    default:
      return 'That could not be saved.'
  }
}
